// markup/CalibrationDialog.jsx — "how long is that line?" scale dialog.
// ---------------------------------------------------------------------------
// Opens after the user draws a reference line with the Calibrate tool. They
// type the line's REAL length + unit; we convert to millimetres and hand it to
// the controller, which stores the calibration as the normalised reference
// segment + knownMm (resolution-independent — see measure.mjs). Uses the shared
// theme primitives so it matches DropPin's other modals.
//
// 2026-09-15 — Brett: "I did a calibrate and m2 is way wrong." The unit sat on
// metres and plan dimensions are millimetres, so a 2090 dimension became 2 km.
// It now starts on the unit last used (millimetres the first time), and scales
// the whole sheet by what is being typed: when that makes the paper kilometres
// or centimetres across, it says so and offers the unit that fits. The button
// still sets any scale (a real site plan can be huge), but Enter will not set
// one that fails the check.
// ---------------------------------------------------------------------------

import React, { useState, useEffect, useRef } from 'react';
import { theme, btnPrimary, btnSecondary, input } from '../theme';
import { MM_PER, checkScale, formatAcross } from './scaleCheck.mjs';

const UNITS = ['mm', 'cm', 'm', 'ft', 'in'];
const UNIT_KEY = 'droppin.markup.calibrationUnit';
// The same 2.09 m dimension, written the way each unit would write it.
const EXAMPLE = { mm: '2090', cm: '209', m: '2.09', ft: '6.86', in: '82.3' };

function rememberedUnit() {
  try {
    const u = window.localStorage.getItem(UNIT_KEY);
    return UNITS.includes(u) ? u : 'mm';
  } catch (_) { return 'mm'; }
}
function rememberUnit(u) {
  try { window.localStorage.setItem(UNIT_KEY, u); } catch (_) { /* private window: millimetres next time */ }
}

export default function CalibrationDialog({
  open, onConfirm, onCancel,
  // The reference line just drawn and the drawing's natural size, so the typed
  // length can be checked against the sheet it implies.
  refA = null, refB = null, naturalW = 0, naturalH = 0,
}) {
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState('mm');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) { setValue(''); setUnit(rememberedUnit()); setTimeout(() => inputRef.current && inputRef.current.focus(), 30); }
  }, [open]);

  if (!open) return null;

  const num = parseFloat(value);
  const valid = isFinite(num) && num > 0;
  const check = valid ? checkScale(num, unit, refA, refB, naturalW, naturalH) : null;
  const imperial = unit === 'ft' || unit === 'in';
  const pickUnit = (u) => { setUnit(u); rememberUnit(u); };
  const confirm = () => { if (valid) { rememberUnit(unit); onConfirm(num * MM_PER[unit]); } };

  return (
    <div
      onPointerDown={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onCancel(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}>
      <div style={{
        background: theme.surface, borderRadius: 12, padding: 20, width: 380, maxWidth: '100%',
        boxShadow: 'var(--shadow-3)', border: `1px solid ${theme.border}`,
      }} onPointerDown={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 800, color: theme.text, marginBottom: 4 }}>Set the drawing scale</div>
        <div style={{ fontSize: 13, color: theme.textLight, marginBottom: 14, lineHeight: 1.5 }}>
          You drew a reference line over a known dimension. Enter its real-world
          length and every measurement on this drawing will read in real units.
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
          <input
            ref={inputRef}
            type="number" inputMode="decimal" min="0" step="any"
            value={value} onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { if (!check || check.plausible) confirm(); }
              else if (e.key === 'Escape') onCancel();
            }}
            placeholder={`e.g. ${EXAMPLE[unit]}`}
            style={{ ...input, flex: 1 }}
          />
          <select value={unit} onChange={(e) => pickUnit(e.target.value)}
            style={{ ...input, width: 88, flex: '0 0 auto' }}>
            {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        {check && check.acrossMm != null && (check.plausible ? (
          <div style={{ fontSize: 12.5, color: theme.textLight, marginTop: 8 }}>
            The sheet works out about {formatAcross(check.acrossMm, imperial)} across.
          </div>
        ) : (
          <div role="alert" style={{
            marginTop: 10, padding: '9px 10px', borderRadius: 8, borderLeft: `3px solid ${theme.warn}`,
            background: 'var(--surface-1)', fontSize: 13, color: theme.text, lineHeight: 1.45,
          }}>
            {num} {unit} makes this sheet about {formatAcross(check.acrossMm, imperial)} across, which can't be right.
            {check.suggestUnit && (
              <div style={{ marginTop: 8 }}>
                <button type="button" style={{ ...btnSecondary, padding: '5px 10px', fontSize: 12.5 }}
                  onClick={() => pickUnit(check.suggestUnit)}>
                  Use {num} {check.suggestUnit}
                </button>
              </div>
            )}
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button type="button" style={btnSecondary} onClick={onCancel}>Cancel</button>
          <button type="button" style={{ ...btnPrimary, opacity: valid ? 1 : 0.5, cursor: valid ? 'pointer' : 'default' }}
            disabled={!valid} onClick={confirm}>Set scale</button>
        </div>
      </div>
    </div>
  );
}
